import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAdminModeEnabled } from '../../selectors/user';
import { ensureINSToken, handleFailedRefresh } from '../../services/interactiveServiceFetch';
import { disconnectUserMcpConnection, fetchUserMcpConnections, startUserMcpConnect } from '../service';

const useUserMcpConnections = () => {
    const [connections, setConnections] = useState([]);
    const [busyProvider, setBusyProvider] = useState(null);
    const adminMode = useSelector(getAdminModeEnabled);

    const refresh = useCallback(async () => {
        try {
            setConnections(await fetchUserMcpConnections(adminMode));
        } catch {
            setConnections([]);
        }
    }, [adminMode]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        // bfcache restore after an OAuth hand-off leaves the page stuck in `busy`.
        const clearBusyOnRestore = event => {
            if (event.persisted) setBusyProvider(null);
        };
        window.addEventListener('pageshow', clearBusyOnRestore);
        return () => window.removeEventListener('pageshow', clearBusyOnRestore);
    }, []);

    const connect = useCallback(
        async provider => {
            setBusyProvider(provider);
            try {
                // Refreshed right before the hand-off: the callback re-authenticates on the way back.
                const refreshResult = await ensureINSToken();
                if (!refreshResult.ok) {
                    handleFailedRefresh(refreshResult);
                    throw new Error('interactive service session could not be refreshed');
                }
                const { authorize_url: authorizeUrl } = await startUserMcpConnect(provider, adminMode);
                window.location.href = authorizeUrl;
            } catch (error) {
                setBusyProvider(null);
                throw error;
            }
        },
        [adminMode]
    );

    const disconnect = useCallback(
        async provider => {
            setBusyProvider(provider);
            try {
                await disconnectUserMcpConnection(provider);
                await refresh();
            } finally {
                setBusyProvider(null);
            }
        },
        [refresh]
    );

    return { connections, busyProvider, refresh, connect, disconnect };
};

export default useUserMcpConnections;
